import * as React from 'react';
import {
  Button,
  Card,
  CardContent,
  Divider,
  Input,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

export interface ColumnDefinition<T> {
  header: string;
  width?: string | number;
  align?: 'left' | 'right' | 'center';
  render: (item: T) => React.ReactNode;
}

interface MasterSectionProps<T extends { id: number }> {
  title: string;
  description: string;
  items: T[];
  columns: ColumnDefinition<T>[];
  onAdd: () => void;
  onEdit: (item: T) => void;
  addLabel?: string;
  editLabel?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  getSearchValue?: (item: T) => string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

const SEARCH_MAX_LENGTH = 160;

export function MasterSection<T extends { id: number }>(props: MasterSectionProps<T>) {
  const {
    title,
    description,
    items,
    columns,
    onAdd,
    onEdit,
    addLabel = 'Add new',
    editLabel = 'Edit',
    emptyMessage = 'No records found yet.',
    searchPlaceholder = 'Search records',
    getSearchValue,
    searchValue,
    onSearchChange,
  } = props;

  const [internalSearch, setInternalSearch] = React.useState('');
  const isControlled = searchValue !== undefined;
  const search = isControlled ? searchValue : internalSearch;

  const handleSearchChange = React.useCallback(
    (value: string) => {
      if (onSearchChange) {
        onSearchChange(value);
      }
      if (!isControlled) {
        setInternalSearch(value);
      }
    },
    [isControlled, onSearchChange],
  );

  const normalizedSearch = search.trim().toLowerCase();
  const filteredItems = React.useMemo(() => {
    if (!normalizedSearch || !getSearchValue) {
      return items;
    }
    return items.filter((item) => {
      try {
        const value = getSearchValue(item) ?? '';
        return value.toLowerCase().includes(normalizedSearch);
      } catch (error) {
        console.warn('Failed to evaluate search value for master record', error);
        return true;
      }
    });
  }, [getSearchValue, items, normalizedSearch]);

  const hasItems = filteredItems.length > 0;
  const isSearching = normalizedSearch.length > 0;

  return (
    <Card variant="outlined" sx={{ borderRadius: 'xl', flex: 1 }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ md: 'center' }}
        >
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            <Typography level="title-lg">{title}</Typography>
            <Typography level="body-sm" color="neutral">{description}</Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
            {getSearchValue ? (
              <Input
                startDecorator={<SearchRoundedIcon />}
                size="sm"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(event) => handleSearchChange(event.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 220 } }}
                aria-label={`Search ${title.toLowerCase()}`}
                slotProps={{ input: { maxLength: SEARCH_MAX_LENGTH } }}
              />
            ) : null}
            <Button onClick={onAdd} variant="soft" color="primary">
              {addLabel}
            </Button>
          </Stack>
        </Stack>
        <Divider />
        {hasItems ? (
          <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'auto' }}>
            <Table
              aria-label={`${title} table`}
              size="sm"
              stickyHeader
              sx={{ '--TableCell-headBackground': 'background.level1' }}
            >
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.header} style={{ textAlign: column.align ?? 'left', width: column.width }}>
                      <Typography level="body-xs" fontWeight={600} textTransform="uppercase">
                        {column.header}
                      </Typography>
                    </th>
                  ))}
                  <th style={{ width: 120 }}>
                    <Typography level="body-xs" fontWeight={600} textTransform="uppercase">
                      Actions
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    {columns.map((column) => (
                      <td key={column.header} style={{ textAlign: column.align ?? 'left' }}>
                        {column.render(item)}
                      </td>
                    ))}
                    <td>
                      <Button size="sm" variant="plain" onClick={() => onEdit(item)}>
                        {editLabel}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Sheet>
        ) : (
          <Sheet
            variant="soft"
            color="neutral"
            sx={{
              borderRadius: 'lg',
              py: 4,
              px: 3,
              textAlign: 'center',
              display: 'grid',
              gap: 1.5,
            }}
          >
            <Typography level="body-sm">
              {isSearching
                ? `No results match “${search.trim()}”.`
                : emptyMessage}
            </Typography>
            {!isSearching ? (
              <Button size="sm" variant="solid" color="primary" onClick={onAdd} sx={{ mx: 'auto' }}>
                {addLabel}
              </Button>
            ) : null}
          </Sheet>
        )}
      </CardContent>
    </Card>
  );
}
